import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Drawer } from "@mui/material";
import { AssistantCloseContext } from "../model/assistantNavigation";
import { setDockOpen, useDockState } from "../model/dockStore";
import {
  ASSISTANT_MODAL_ATTR,
  useAssistantHotkeys,
} from "../model/useAssistantHotkeys";
import { useAssistantComposer } from "../model/useAssistantComposer";
import { useIsAssistantDesktop } from "../model/useAssistantDockInset";
import { usePartnerAssistant } from "../model/usePartnerAssistant";
import { useVisualViewport } from "../model/useVisualViewport";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantDock } from "./AssistantDock";
import { AssistantFab } from "./AssistantFab";
import { AssistantPanel } from "./AssistantPanel";

const PANEL_ID = "partner-assistant-panel";

const noop = () => undefined;

/** Фокус в поле с курсором в конце черновика. */
const focusAtEnd = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.focus();
  const end = el.value.length;
  el.setSelectionRange(end, end);
};

/**
 * Помощник партнёра (п. 9 спеки).
 *
 * Круглая кнопка в правом нижнем углу (как у помощника зрителя на
 * витрине) или горячая клавиша открывают помощника с фокусом в поле внизу
 * панели. Пока помощник открыт, кнопки нет: закрывают его кнопкой в
 * панели, Esc или горячей клавишей, фокус возвращается на кнопку.
 *
 * Десктоп: боковая панель справа на всю высоту (как чат в редакторе кода),
 * страница сужается на её ширину (Layout, useAssistantDockInset), кабинет
 * остаётся рабочим. Открытость и ширина панели запоминаются в браузере;
 * переходы по ссылкам ленты её не закрывают.
 *
 * Мобила: лист на весь экран с той же панелью.
 *
 * Черновик (текст, фото, загрузки) и диалог живут здесь, выше панели:
 * они переживают закрытие панели и листа.
 */
export const PartnerAssistant: FC = () => {
  const { t } = useTranslation();
  const isDesktop = useIsAssistantDesktop();
  const isMobile = !isDesktop;
  const assistant = usePartnerAssistant();
  const { open: dockOpen } = useDockState();
  const [sheetOpen, setSheetOpen] = useState(false);
  const fab = useRef<HTMLButtonElement>(null);
  const panelInput = useRef<HTMLTextAreaElement>(null);
  const { pathname, search } = useLocation();
  const viewport = useVisualViewport(isMobile);

  const composer = useAssistantComposer({
    conversationId: assistant.state.conversationId,
    busy: assistant.state.busy,
    onSend: (text, photos) => void assistant.send(text, photos),
  });

  // Лист на мобиле закрывает страницу целиком: переход по ссылке его
  // сворачивает. Боковая панель десктопа стоит рядом со страницей и
  // остаётся открытой.
  useEffect(() => {
    setSheetOpen(false);
  }, [pathname, search]);

  const openDock = useCallback(() => {
    flushSync(() => setDockOpen(true));
    focusAtEnd(panelInput.current);
  }, []);

  const closeDock = useCallback(() => {
    flushSync(() => setDockOpen(false));
    // Панель пропадает вместе с фокусом: он не теряется в body, а
    // возвращается на круглую кнопку, которая появляется снова.
    fab.current?.focus();
  }, []);

  const closeSheet = useCallback(() => {
    flushSync(() => setSheetOpen(false));
    fab.current?.focus();
  }, []);

  // Ссылка ленты уводит на другую страницу: лист сворачивается, фокус
  // остаётся за новой страницей.
  const closeSheetOnNavigate = useCallback(() => setSheetOpen(false), []);

  // Горячая клавиша: закрытая панель открывается с фокусом в поле; из
  // открытой панели клавиша её закрывает; если фокус на странице, он
  // переходит в поле панели.
  const onHotkey = useCallback(() => {
    if (isMobile) {
      setSheetOpen(true);
      requestAnimationFrame(() => focusAtEnd(panelInput.current));
      return;
    }
    const panel = document.getElementById(PANEL_ID);
    if (dockOpen && panel?.contains(document.activeElement)) closeDock();
    else openDock();
  }, [isMobile, dockOpen, openDock, closeDock]);
  useAssistantHotkeys(onHotkey);

  const composerField = (
    <AssistantComposer
      ref={panelInput}
      composer={composer}
      busy={assistant.state.busy}
    />
  );

  if (isMobile) {
    // iOS открывает клавиатуру только на focus() внутри обработчика
    // нажатия: лист открывается синхронно, и поле фокусируется в нём же.
    const openSheet = () => {
      flushSync(() => setSheetOpen(true));
      focusAtEnd(panelInput.current);
    };
    return (
      <>
        {!sheetOpen && (
          <AssistantFab
            ref={fab}
            expanded={sheetOpen}
            controlsId={PANEL_ID}
            onClick={openSheet}
          />
        )}
        <Drawer
          anchor="bottom"
          open={sheetOpen}
          onClose={closeSheet}
          // Лента и черновик переживают сворачивание листа.
          keepMounted
          disableAutoFocus
          // Кнопки, на которую MUI вернул бы фокус, нет: closeSheet
          // возвращает его на появившуюся кнопку сам.
          disableRestoreFocus
          {...{ [ASSISTANT_MODAL_ATTR]: true }}
          slotProps={{
            paper: {
              id: PANEL_ID,
              role: "dialog",
              "aria-label": t("partnerAssistant.title"),
              // Высота по visualViewport: с клавиатурой iOS поле внизу листа
              // остаётся над ней; без API весь экран (100dvh).
              sx: viewport
                ? {
                    top: `${viewport.offsetTop}px`,
                    bottom: "auto",
                    height: `${viewport.height}px`,
                  }
                : { height: "100dvh" },
            },
          }}
        >
          <AssistantCloseContext.Provider value={closeSheetOnNavigate}>
            <AssistantPanel
              assistant={assistant}
              onClose={closeSheet}
              composer={composerField}
            />
          </AssistantCloseContext.Provider>
        </Drawer>
      </>
    );
  }

  return (
    <>
      {!dockOpen && (
        <AssistantFab
          ref={fab}
          expanded={dockOpen}
          controlsId={PANEL_ID}
          onClick={openDock}
        />
      )}
      {dockOpen && (
        // Ссылки ленты в кабинет панель не закрывают: она не мешает странице.
        <AssistantCloseContext.Provider value={noop}>
          <AssistantDock id={PANEL_ID} onClose={closeDock}>
            <AssistantPanel
              assistant={assistant}
              onClose={closeDock}
              composer={composerField}
            />
          </AssistantDock>
        </AssistantCloseContext.Provider>
      )}
    </>
  );
};
